import { useEffect, useMemo, useRef, useState } from "react";
import s from "./classes-board.module.scss";
import classNames from "classnames";
import { addDays, endOfWeek } from "date-fns";
import {
  IconCalendarShare,
  IconChevronLeft,
  IconChevronRight,
  IconEyeFilled,
  IconPlus,
	IconSettings,
} from "@tabler/icons-react";
import months from "@shared/model/months.json";
import { useTimeSlots } from "@features/schedule/api/useTimeSlots";
import { useGroupedTimeSlots } from "@features/schedule/api/useGroupedTimeSlots";
import { useUpcomingAppointments } from "@entities/schedule/api/useUpcomingAppointments";
import { Button } from "@shared/ui/button/button";
import { Loader } from "@shared/ui/loader/loader";
import { useEditScheduleModal } from "@features/teacher/edit-schedule/model/use-edit-schedule-modal";
import { useCalendarSyncModal } from "@features/calendar-sync";
import { EditScheduleModal } from "@features/teacher/edit-schedule";
import { TZDate } from "@date-fns/tz";
import { queryClient } from "@shared/api/query-client";
import { DaysOfWeek } from "../days-of-week/days-of-week";

const getMonthAndDate = (d) => {
  const month = d.getMonth();
  const date = d.getDate();

  return [
    months.find((el) => el.number === month).genitive.toLowerCase(),
    date,
  ];
};

export const ClassesBoard = ({
  appointments,
  appointmentsIsFetched,
  isTeacher,
  start_date,
  end_date,
  set_end_date,
  set_start_date,
  className,
}) => {
	const { open: openEditScheduleModal } = useEditScheduleModal()
	const { open: openCalendarSyncModal } = useCalendarSyncModal()

  const { data: timeSlots } = useTimeSlots(isTeacher);
  const { data: profile } = queryClient?.getQueryData(['profile']);
  const groupedTimeSlots = useGroupedTimeSlots(timeSlots, {withoutReserved: true});
  const todayRef = useRef(null);
  const { data: upcoming } = useUpcomingAppointments();
  const [showType, setShowType] = useState("eye");

  const todayWithTime = new Date();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const goToPreviousWeek = () => {
    const previousWeekStart = addDays(start_date, -7);
    const previousWeekEnd = endOfWeek(previousWeekStart, {weekStartsOn: 1});

    set_start_date(previousWeekStart);
    set_end_date(previousWeekEnd);
  };

  // Функция для перехода к следующей неделе
  const goToNextWeek = () => {
    const nextWeekStart = addDays(start_date, 7);
    // const nextWeekEnd = addDays(start_date, 13);
    const nextWeekEnd = endOfWeek(nextWeekStart, {weekStartsOn: 1});

    set_start_date(nextWeekStart);
    set_end_date(nextWeekEnd);
  };

  const weekClasses = useMemo(() => {
    let startDate = new Date(start_date);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(end_date);
    endDate.setHours(0, 0, 0, 0);

    let classesByDay = {};
    while (startDate <= endDate) {
      let dayName = startDate.toLocaleString("ru-RU", { weekday: "long" });
      classesByDay[dayName] = appointments?.filter((classItem) => {
        const classDate = new TZDate(classItem.starts_at, profile?.timezone);
        return (
          classDate.toLocaleString("ru-RU", {
            year: "numeric",
            month: "long",
            day: "numeric",
          }) ===
          startDate.toLocaleString("ru-RU", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })
        );
      });
      startDate.setDate(startDate.getDate() + 1);
    }		

    return classesByDay;
  }, [appointments, start_date, end_date, profile?.timezone]);



  useEffect(() => {
    if (todayRef?.current) {
      todayRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
      });
    }
  }, [todayRef, appointmentsIsFetched]);

  return (
    <div className={classNames(s.weekCard, className)}>
      {(!isTeacher || groupedTimeSlots) ? (
        <>
          <div className={s.weekHeader}>
            {start_date && end_date && (
              <div className={s.selectWrapper}>
                <Button
                  onClick={() => goToPreviousWeek()}
                  label={<IconChevronLeft size={10} />}
                  theme="secondary"
                  className={s.monthBtn}
                />
                <span>
                  {`${getMonthAndDate(start_date)[1]} ${
                    getMonthAndDate(start_date)[0]
                  } - ${getMonthAndDate(end_date)[1]} ${
                    getMonthAndDate(end_date)[0]
                  }`}
                </span>
                <Button
                  onClick={() => goToNextWeek()}
                  label={<IconChevronRight size={10} />}
                  theme="secondary"
                  className={s.monthBtn}
                />
              </div>
            )}
            <div className={s.weekHeaderRight}>
              {isTeacher ? (
                <>
									<Button
										lefticon={
											showType === "eye" ? (
												<IconPlus size={16} strokeWidth={3} />
											) : (
												<IconEyeFilled size={16} />
											)
										}
										theme="secondary"
										size="small"
										className={s.circleButton}
										onClick={() => setShowType(showType === "eye" ? "add" : "eye")}
									/>
                <Button
									lefticon={<IconSettings size={24} />}
									theme="simple"
									size="small"
                  onClick={() => openEditScheduleModal()}
								/>
								<EditScheduleModal/>
                </>
              ) : (
                ""
              )}
              <Button
                lefticon={<IconCalendarShare size={24} />}
                theme="simple"
                size="small"
                onClick={() => openCalendarSyncModal()}
              />
            </div>
          </div>
          <div className={s.hLine}></div>
          <DaysOfWeek {...{appointmentsIsFetched, start_date, end_date, today, isTeacher, groupedTimeSlots, todayRef, todayWithTime, showType, weekClasses, upcoming}}/>
        </>
      ) : (
        <Loader />
      )}
    </div>
  );
};